import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const pillars = {
  marketing: {
    label: "Marketing & AEO",
    headline: "Generative Engine Optimization & Autonomous Acquisition",
    agents: [
      {
        role: "AEO Audit & Share-of-Model",
        desc: "Measure and optimize how frequently ChatGPT, Claude, and Perplexity recommend and cite your brand.",
        icon: "/images/agents_hq/visual_gen.png",
        path: "/agents/aeo"
      },
      {
        role: "Autonomous Marketing Agent",
        desc: "Answer-engine visibility, multi-channel content generation, and brand narrative governance.",
        icon: "/images/agents_hq/visual_gen.png",
        path: "/marketing"
      },
      {
        role: "Sales Development (SDR)",
        desc: "Autonomous outbound prospect research, ICP qualification, and multi-channel pipeline growth.",
        icon: "/images/agents_hq/email_assistant.png",
        path: "/agents/sdr-agent"
      },
      {
        role: "Market & Web Intelligence",
        desc: "Continuous competitor intelligence, price scraping, and market trend synthesis.",
        icon: "/images/agents_hq/web_scraper.png",
        path: "/agents/web-scraper"
      }
    ]
  },
  finance: {
    label: "Finance & Capital Ops",
    headline: "Autonomous Accounting, Compliance & FP&A Systems",
    agents: [
      {
        role: "Autonomous CPA & Accounting",
        desc: "Institutional financial memory, multi-entity ledger reconciliation, and variance anomaly detection.",
        icon: "/images/agents_hq/accountant.png",
        path: "/agents/cpa"
      },
      {
        role: "KYC & Regulatory Onboarding",
        desc: "Automated identity document extraction, risk scoring, and MAS-aligned compliance form generation.",
        icon: "/images/agents_hq/kyc.png",
        path: "/agents/kyc-onboarding"
      },
      {
        role: "Copilot for Finance Masterclass",
        desc: "Executive 1-day onsite training for CFOs and finance teams — variance analysis, rolling forecasts, and board decks.",
        icon: "/images/agents_hq/metric_modeler.png",
        path: "/copilot-finance-training"
      },
      {
        role: "Financial Services AI Suite",
        desc: "Sovereign AI workflows tailored for private wealth, banking, asset management, and fintech.",
        icon: "/images/agents_hq/doc_parser.png",
        path: "/industries/financial-services-ai-agents"
      }
    ]
  },
  operations: {
    label: "Operations & Cloud",
    headline: "Governed Enterprise Cloud, Data & Document Automation",
    agents: [
      {
        role: "Cloud Architect Agent",
        desc: "Multi-cloud Well-Architected validation, automated FinOps cost optimization, and infrastructure topology design.",
        icon: "/images/agents_hq/cloud_architect.png",
        path: "/agents/cloud-architect"
      },
      {
        role: "Data Engineer & ETL Pipelines",
        desc: "Automate complex transformation workflows, schema migrations, and governed data pipelines.",
        icon: "/images/agents_hq/data_engineer.png",
        path: "/agents/data-engineer"
      },
      {
        role: "Document & Contract Intelligence",
        desc: "Parse unstructured invoices, procurement contracts, and technical specifications with zero hallucinations.",
        icon: "/images/agents_hq/doc_parser.png",
        path: "/agents/doc-parsing"
      },
      {
        role: "Operational Cost Reduction",
        desc: "Targeted workflow automation across cloud spend, IT service desks, and procurement operations.",
        icon: "/images/agents_hq/summarizer.png",
        path: "/use-cases/reduce-operational-costs"
      }
    ]
  },
  leadership: {
    label: "AI Leadership & Governance",
    headline: "Executive Advisory, Policy-as-Code & Workforce Upskilling",
    agents: [
      {
        role: "Fractional Chief AI Officer",
        desc: "Embed an executive AI leader into your team for roadmap design, architecture oversight, and governance.",
        icon: "/images/agents_hq/voice_interviewer.png",
        path: "/fractional-caio"
      },
      {
        role: "ChatGPT Enterprise Training",
        desc: "Enterprise 1-day onsite team training to build custom GPTs on internal files and establish verification standards.",
        icon: "/images/agents_hq/email_assistant.png",
        path: "/chatgpt-enterprise-training"
      },
      {
        role: "LLM-as-a-Judge & Evals",
        desc: "Continuous model evaluation, hallucination detection, benchmark scoring, and safety guardrails.",
        icon: "/images/agents_hq/voice_interviewer.png",
        path: "/agents/llm-as-judge"
      },
      {
        role: "Security & Sovereign Governance",
        desc: "Policy-as-Code guardrails, RBAC, PII redaction, and air-gapped on-premise deployment standards.",
        icon: "/images/agents_hq/dba.png",
        path: "/security-and-governance"
      }
    ]
  }
};

const DigitalWorkforce = () => {
  const [activeTab, setActiveTab] = useState('marketing');
  const navigate = useNavigate();
  const currentPillar = pillars[activeTab] || pillars.marketing;

  return (
    <section className="digital-workforce" id="agents" style={{ 
      padding: '100px 0', 
      background: '#f8f9fa', 
      fontFamily: 'inherit'
    }}>
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div style={{ 
            display: 'inline-block',
            background: 'rgba(0,113,227,0.08)',
            color: '#0071e3',
            fontSize: '0.85rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '6px 16px',
            borderRadius: '20px',
            marginBottom: '16px'
          }}>
            Enterprise Transformation Pillars
          </div>
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1d1d1f', marginBottom: '16px' }}>
            Domain-Specific Autonomous Capabilities
          </h2>
          <p style={{ fontSize: '1.2rem', color: '#666', maxWidth: '800px', margin: '0 auto' }}>
            {currentPillar.headline}
          </p>
        </div>

        {/* 4 Pillars Navigation Tabs */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          flexWrap: 'wrap',
          gap: '12px', 
          marginBottom: '48px' 
        }}>
          {Object.entries(pillars).map(([key, data]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: '12px 24px',
                borderRadius: '30px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: activeTab === key ? '#0071e3' : 'white',
                color: activeTab === key ? 'white' : '#475569',
                border: activeTab === key ? '1px solid #0071e3' : '1px solid #e2e8f0',
                boxShadow: activeTab === key ? '0 4px 12px rgba(0,113,227,0.25)' : '0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              {data.label}
            </button>
          ))}
        </div>

        {/* Agent Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '24px'
        }}>
          {currentPillar.agents.map((agent, index) => (
            <div 
              key={index}
              onClick={() => navigate(agent.path)}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '36px 24px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0,0,0,0.04)',
                border: '1px solid #f1f5f9',
                transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 16px 32px rgba(0,113,227,0.08)';
                e.currentTarget.style.borderColor = '#93c5fd';
                const glow = e.currentTarget.querySelector('.agent-glow');
                if (glow) glow.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.04)';
                e.currentTarget.style.borderColor = '#f1f5f9';
                const glow = e.currentTarget.querySelector('.agent-glow');
                if (glow) glow.style.opacity = '0';
              }}
            >
              {/* Soft Glow Effect */}
              <div className="agent-glow" style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'radial-gradient(circle at center, rgba(0,113,227,0.06) 0%, transparent 70%)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
                pointerEvents: 'none'
              }}></div>

              <div style={{
                width: '88px',
                height: '88px',
                borderRadius: '50%',
                border: '2px solid #e2e8f0',
                overflow: 'hidden',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8fafc'
              }}>
                <img
                  src={agent.icon}
                  alt={agent.role}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#0f172a', marginBottom: '10px', lineHeight: '1.3' }}>
                {agent.role}
              </h3>
              <p style={{ fontSize: '0.9rem', color: '#64748b', lineHeight: '1.55', margin: 0, flex: 1 }}>
                {agent.desc}
              </p>
              <div style={{ 
                marginTop: '20px', 
                fontSize: '0.85rem', 
                fontWeight: '700', 
                color: '#0071e3',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                Explore Solution →
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DigitalWorkforce;
